<script>
  import { Trash2, Plus, ChevronDown, Link2, AlertTriangle } from '../lib/icons/index.js';
  import { t } from '../i18n/index.js';
  import { CORRELATI_TYPES, emptyCorrelato, filledCorrelati } from '../lib/correlati.js';
  import InfoHint from './InfoHint.svelte';

  /**
   * The correlated-document references (order, contract, convention, receipt,
   * linked invoices), for every screen that collects them.
   *
   * It was the creation form's own section until the draft editor needed it:
   * the references on a draft are what promotion turns into DatiContratto and
   * its siblings, so a draft prepared through the API with a CIG has to be
   * visible and editable where the operator finishes it, or the CIG a public
   * administration requires is one the operator cannot see. The rows, and the
   * body they produce, are `lib/correlati.js`; this file only collects them.
   */
  export let correlati = [emptyCorrelato()];
  /** TipoDocumento and FormatoTrasmissione decide whether the section is pushed open. */
  export let tipoDocumento = 'TD01';
  export let formatoTrasmissione = 'FPR12';
  /** Whether the section is unfolded; bindable, so a parent can open it on a refusal. */
  export let open = false;
  // Set the moment the operator opens or closes the section by hand, so the
  // automatic expansion below never fights their choice.
  let toggled = false;

  $: filled = filledCorrelati(correlati);

  function add() { correlati = [...correlati, emptyCorrelato()]; }

  function remove(i) {
    correlati = correlati.length <= 1 ? [emptyCorrelato()] : correlati.filter((_, idx) => idx !== i);
  }

  function toggle() {
    open = !open;
    toggled = true;
  }

  // A credit note must say which invoice it corrects, and an invoice to a
  // public administration is refused by the administration itself when the CIG
  // is missing. Both are cases where hiding the section behind a closed toggle
  // is how the reference gets forgotten, so open it instead.
  $: relevant = tipoDocumento === 'TD04' || formatoTrasmissione === 'FPA12';
  $: if (relevant && !toggled) open = true;

  const inputCls = 'input';
  const inputStyle = '';
  const labelCls = 'block eyebrow mb-1';
  const labelStyle = 'color: var(--color-text-muted);';
</script>

  <div class="card p-6 mb-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <h2 class="eyebrow eyebrow-lg inline-flex items-center gap-1" style="color: var(--color-primary);">
        <Link2 size={16} />{$t('invoiceCreate.sections.correlati')}<InfoHint term="documentiCorrelati" />
      </h2>
      <button type="button" on:click={toggle}
        aria-expanded={open} aria-controls="correlati-panel"
        class="flex items-center gap-1 eyebrow hover:text-action transition-colors"
        style="color: var(--color-text-muted);">
        <span>{filled.length > 0 ? $t('invoiceCreate.correlati.countSome', { n: filled.length }) : $t('invoiceCreate.correlati.countNone')}</span>
        <ChevronDown size={14} class="transition-transform {open ? 'rotate-180' : ''}" />
        <span class="sr-only">{open ? $t('invoiceCreate.correlati.hide') : $t('invoiceCreate.correlati.show')}</span>
      </button>
    </div>

    <div id="correlati-panel" hidden={!open}>
      <p class="mt-3 text-xs measure" style="color: var(--color-text-muted);">{$t('invoiceCreate.correlati.intro')}</p>

      <div class="alert alert-accent mt-3">
        <AlertTriangle size={18} class="alert-mark mt-0.5" />
        <p class="text-xs measure" style="color: var(--color-text-body);">{$t('invoiceCreate.correlati.paWarning')}</p>
      </div>

      {#if tipoDocumento === 'TD04'}
        <p class="mt-3 text-xs measure" style="color: var(--color-primary);">{$t('invoiceCreate.correlati.creditNoteHint')}</p>
      {:else if formatoTrasmissione === 'FPA12'}
        <p class="mt-3 text-xs measure" style="color: var(--color-primary);">{$t('invoiceCreate.correlati.paHint')}</p>
      {/if}

      {#each correlati as ref, i}
        <div class="mt-4 border-t pt-4" style="border-color: var(--color-tint-soft);">
          <div class="flex items-center justify-between mb-2">
            <span class="eyebrow" style="color: var(--color-text-muted);">{$t('invoiceCreate.correlati.referenceLabel', { n: i + 1 })}</span>
            <button type="button" on:click={() => remove(i)} class="p-1 hover:text-danger" style="color: var(--color-text-muted);"
              aria-label={$t('invoiceCreate.correlati.remove', { n: i + 1 })}>
              <Trash2 size={14} />
            </button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div class="md:col-span-2">
              <label class={labelCls} style={labelStyle} for="correlato-tipo-{i}">{$t('invoiceCreate.correlati.fields.tipo')}</label>
              <select id="correlato-tipo-{i}" bind:value={ref.tipo} class={inputCls} style={inputStyle}>
                {#each CORRELATI_TYPES as tipo}<option value={tipo}>{$t(`invoiceCreate.correlati.types.${tipo}`)}</option>{/each}
              </select>
            </div>
            <div class="md:col-span-2">
              <label class={labelCls} style={labelStyle} for="correlato-id-{i}">{$t('invoiceCreate.correlati.fields.idDocumento')}</label>
              <input id="correlato-id-{i}" bind:value={ref.idDocumento} maxlength="20" class={inputCls} style={inputStyle}
                placeholder={ref.tipo === 'fattureCollegate'
                  ? $t('invoiceCreate.correlati.placeholders.idDocumentoFattura')
                  : $t('invoiceCreate.correlati.placeholders.idDocumento')} />
              <p class="mt-1 text-xs measure" style="color: var(--color-text-muted);">{$t('invoiceCreate.correlati.idDocumentoHint')}</p>
            </div>
            <div class="md:col-span-2">
              <label class={labelCls} style={labelStyle} for="correlato-data-{i}">{$t('invoiceCreate.correlati.fields.data')}</label>
              <input id="correlato-data-{i}" bind:value={ref.data} type="date" class={inputCls} style={inputStyle} />
            </div>
            <div class="md:col-span-3">
              <span class="{labelCls} inline-flex items-center gap-1" style={labelStyle}>
                <label for="correlato-cig-{i}">{$t('invoiceCreate.correlati.fields.codiceCIG')}</label><InfoHint term="codiceCIG" />
              </span>
              <input id="correlato-cig-{i}" bind:value={ref.codiceCIG} maxlength="15" class={inputCls} style={inputStyle}
                placeholder={$t('invoiceCreate.correlati.placeholders.codiceCIG')} />
            </div>
            <div class="md:col-span-3">
              <span class="{labelCls} inline-flex items-center gap-1" style={labelStyle}>
                <label for="correlato-cup-{i}">{$t('invoiceCreate.correlati.fields.codiceCUP')}</label><InfoHint term="codiceCUP" />
              </span>
              <input id="correlato-cup-{i}" bind:value={ref.codiceCUP} maxlength="15" class={inputCls} style={inputStyle}
                placeholder={$t('invoiceCreate.correlati.placeholders.codiceCUP')} />
            </div>
          </div>

          <p class="mt-2 text-xs italic measure" style="color: var(--color-text-muted);">{$t(`invoiceCreate.correlati.typeHelp.${ref.tipo}`)}</p>
        </div>
      {/each}

      <button type="button" on:click={add} class="mt-4 flex items-center gap-1 eyebrow hover:text-action transition-colors" style="color: var(--color-text-muted);">
        <Plus size={14} /> {$t('invoiceCreate.correlati.add')}
      </button>
    </div>
  </div>
